import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Clock, Target as TargetIcon, Swords, Trophy, Trash2, Crosshair, Crown, XCircle } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const ICONS = {
  qualification: Trophy,
  recurve: Swords,
  compound: TargetIcon,
  solo: TargetIcon,
};

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}


// ─── Mini viewer cible pour l'historique ─────────────────────────────────────
function getTargetZonesH(withX) {
  const base = [
    { v:10,isX:true, l:"X",  c:"#FDE047",rN:0.05 },
    { v:10,isX:false,l:"10", c:"#FDE047",rN:0.10 },
    { v:9, isX:false,l:"9",  c:"#FDE047",rN:0.20 },
    { v:8, isX:false,l:"8",  c:"#EF4444",rN:0.30 },
    { v:7, isX:false,l:"7",  c:"#EF4444",rN:0.40 },
    { v:6, isX:false,l:"6",  c:"#60A5FA",rN:0.50 },
    { v:5, isX:false,l:"5",  c:"#60A5FA",rN:0.60 },
    { v:4, isX:false,l:"4",  c:"#18181b",rN:0.70 },
    { v:3, isX:false,l:"3",  c:"#18181b",rN:0.80 },
    { v:2, isX:false,l:"2",  c:"#d4d4d8",rN:0.90 },
    { v:1, isX:false,l:"1",  c:"#d4d4d8",rN:1.00 },
  ];
  return withX ? base : base.filter(z => !z.isX);
}

function HistoryTargetViewer({ item, onClose }) {
  const { t } = useI18n();
  const canvasRef = React.useRef(null);
  const withX = item.settings?.with_x !== false;
  const zones = getTargetZonesH(withX);
  const scale = 1.0;
  const R = 148;

  React.useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const W = cvs.width, cx = W/2, cy = W/2;
    ctx.clearRect(0,0,W,W);
    ctx.beginPath(); ctx.arc(cx,cy,R+2,0,Math.PI*2); ctx.fillStyle="#111"; ctx.fill();
    for(let i=zones.length-1;i>=0;i--){
      const z=zones[i], r=R*z.rN*scale;
      ctx.beginPath(); ctx.arc(cx,cy,r,0,Math.PI*2); ctx.fillStyle=z.c; ctx.fill();
      const isBlack=z.l==="4"||z.l==="3";
      ctx.strokeStyle=isBlack?"rgba(255,255,255,0.65)":"rgba(0,0,0,0.22)";
      ctx.lineWidth=isBlack?0.8:0.5; ctx.stroke();
    }
    // Points
    const rawScores = item.scores || [];
    const seriesList = Array.isArray(rawScores[0]?.[0]) ? rawScores : [rawScores];
    const rawCoords = item.coords || [];
    const coordsList = Array.isArray(rawCoords[0]?.[0]) ? rawCoords : [rawCoords];
    seriesList.forEach((serie,si) => {
      serie.forEach((end,ei) => {
        end.forEach((v,ai) => {
          if(v===null||v===undefined) return;
          const c=coordsList[si]?.[ei]?.[ai];
          if(!c||(c.nx===0.5&&c.ny===0.5)) return;
          const ax=cx+(c.nx-0.5)*R*2, ay=cy+(c.ny-0.5)*R*2;
          ctx.beginPath(); ctx.arc(ax,ay,2,0,Math.PI*2); ctx.fillStyle="#22d3ee"; ctx.fill();
          ctx.lineWidth=1.2; ctx.strokeStyle="#000"; ctx.stroke();
        });
      });
    });
  }, [zones, scale, item]);

  return (
    <div style={{position:"fixed",inset:0,zIndex:50,background:"rgba(0,0,0,0.88)",display:"flex",alignItems:"center",justifyContent:"center",padding:"16px"}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{width:"100%",maxWidth:"360px",background:"#0a0a0a",border:"1px solid #3f3f46",borderRadius:"16px",padding:"16px",display:"flex",flexDirection:"column",gap:"12px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div>
            <span style={{color:"#71717a",fontSize:"11px",textTransform:"uppercase",letterSpacing:".2em"}}>{t("target.impacts_title")}</span>
            {item.session_name && <div style={{color:"#d4d4d8",fontSize:"13px",marginTop:"2px"}}>{item.session_name}</div>}
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:"#71717a",cursor:"pointer",fontSize:"22px",lineHeight:1}}>×</button>
        </div>
        <canvas ref={canvasRef} width={320} height={320}
          style={{display:"block",width:"100%",aspectRatio:"1",borderRadius:"50%"}} />
        <p style={{color:"#52525b",fontSize:"11px",textAlign:"center",textTransform:"uppercase",letterSpacing:".1em",margin:0}}>
          Seules les flèches placées sur la cible sont affichées
        </p>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, onClick, hint }) {
  const clickable = !!onClick;
  return (
    <div onClick={onClick}
      className={`glass p-4 ${clickable ? "cursor-pointer hover:border-cyan-400/60 hover:bg-zinc-900 transition-colors" : ""}`}>
      <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-heading font-black text-3xl tabular-nums mt-1">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-0.5">{sub}</div>}
      {clickable && hint && <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 mt-1">{hint} →</div>}
    </div>
  );
}

function StatImpactTarget({ impact }) {
  const ref = React.useRef(null);
  const zones = getTargetZonesH(true);
  const R = 148;
  React.useEffect(() => {
    const cvs = ref.current; if (!cvs) return;
    const ctx = cvs.getContext("2d"); const W = cvs.width, cx = W / 2, cy = W / 2;
    ctx.clearRect(0, 0, W, W);
    ctx.beginPath(); ctx.arc(cx, cy, R + 2, 0, Math.PI * 2); ctx.fillStyle = "#111"; ctx.fill();
    for (let i = zones.length - 1; i >= 0; i--) {
      const z = zones[i], r = R * z.rN;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fillStyle = z.c; ctx.fill();
      const isBlack = z.l === "4" || z.l === "3";
      ctx.strokeStyle = isBlack ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.22)";
      ctx.lineWidth = isBlack ? 0.8 : 0.5; ctx.stroke();
    }
    (impact.points || []).forEach((p) => {
      const ax = cx + (p.nx - 0.5) * R * 2, ay = cy + (p.ny - 0.5) * R * 2;
      ctx.beginPath(); ctx.arc(ax, ay, 1.6, 0, Math.PI * 2); ctx.fillStyle = "rgba(34,211,238,0.4)"; ctx.fill();
    });
    const rr = Math.min(R, impact.avg_radius * R * 2);
    ctx.beginPath(); ctx.setLineDash([4, 4]); ctx.arc(cx, cy, rr, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255,255,255,0.55)"; ctx.lineWidth = 1; ctx.stroke(); ctx.setLineDash([]);
    const mx = cx + (impact.marker.nx - 0.5) * R * 2, my = cy + (impact.marker.ny - 0.5) * R * 2;
    ctx.beginPath(); ctx.arc(mx, my, 6, 0, Math.PI * 2); ctx.fillStyle = "#22d3ee"; ctx.fill();
    ctx.lineWidth = 2; ctx.strokeStyle = "#000"; ctx.stroke();
    ctx.beginPath(); ctx.arc(mx, my, 9, 0, Math.PI * 2); ctx.strokeStyle = "#22d3ee"; ctx.lineWidth = 1; ctx.stroke();
  }, [impact, zones]);
  return <canvas ref={ref} width={320} height={320} style={{ display: "block", width: "100%", maxWidth: "260px", margin: "0 auto", aspectRatio: "1", borderRadius: "50%" }} />;
}

function ScoreCurve({ curve }) {
  if (!curve || curve.length === 0) return null;
  const W = 600, H = 220, pad = 38;
  const ys = curve.map((c) => c.score360);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const lo = Math.max(0, Math.floor(minY - 5));
  const hi = Math.max(lo + 1, Math.ceil(maxY + 5));
  const span = hi - lo;
  const px = (i) => (curve.length === 1 ? W / 2 : pad + i * (W - 2 * pad) / (curve.length - 1));
  const py = (v) => H - pad - (v - lo) / span * (H - 2 * pad);
  const pts = curve.map((c, i) => `${px(i)},${py(c.score360)}`).join(" ");
  const fmt = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return ""; } };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%" }}>
      {[0, 0.25, 0.5, 0.75, 1].map((f, k) => {
        const y = pad + f * (H - 2 * pad); const val = Math.round(hi - f * span);
        return (<g key={k}>
          <line x1={pad} y1={y} x2={W - pad} y2={y} stroke="rgba(255,255,255,0.08)" />
          <text x={8} y={y + 4} fill="#52525b" fontSize="11">{val}</text>
        </g>);
      })}
      {curve.length > 1 && <polyline points={pts} fill="none" stroke="#22d3ee" strokeWidth="2" />}
      {curve.map((c, i) => <circle key={i} cx={px(i)} cy={py(c.score360)} r="3.5" fill="#22d3ee" />)}
      <text x={pad} y={H - 10} fill="#52525b" fontSize="11">{fmt(curve[0].date)}</text>
      <text x={W - pad} y={H - 10} fill="#52525b" fontSize="11" textAnchor="end">{fmt(curve[curve.length - 1].date)}</text>
    </svg>
  );
}

const PERIODS = ["3d", "1w", "2w", "1m", "3m", "6m", "all"];

function StatsPanel({ stats, loading, period, onPeriod }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const imp = stats?.impact;
  const hasTendency = imp && imp.concentration >= 0.15;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {PERIODS.map((p) => (
          <button key={p} onClick={() => onPeriod(p)}
            className={`px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${period === p ? "bg-cyan-400 text-black" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"}`}>
            {t(`stats.p${p}`)}
          </button>
        ))}
      </div>

      {loading && <div className="text-zinc-500 uppercase tracking-widest">{t("common.loading")}</div>}

      {!loading && (!stats || stats.total_arrows === 0) && (
        <div className="glass p-10 text-center text-zinc-400">{t("stats.no_data")}</div>
      )}

      {!loading && stats && stats.total_arrows > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard label={t("stats.arrows_counted")} value={stats.total_arrows} sub={`${stats.sessions} ${t("stats.sessions").toLowerCase()}`} />
            <StatCard label={t("stats.avg_per_arrow")} value={stats.avg_per_arrow} />
            <StatCard label={t("stats.avg_360")} value={stats.avg_360} />
            <StatCard label={t("stats.best_360")} value={stats.best_360}
              onClick={stats.best_id ? () => navigate(`/solo/${stats.best_id}`) : undefined}
              hint={stats.best_id ? t("history.open") : undefined} />
            <StatCard label={t("stats.tens")} value={stats.tens} sub={`${stats.tens_pct}%`} />
            <StatCard label={t("stats.xs")} value={stats.xs} sub={`${stats.xs_pct}%`} />
          </div>

          {imp && (
            <div className="glass p-4 md:p-6">
              <h3 className="font-heading font-black text-lg uppercase tracking-tight mb-1">{t("stats.impact_title")}</h3>
              <p className="text-xs text-zinc-500 mb-4">{t("stats.impact_note")}</p>
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <StatImpactTarget impact={imp} />
                <div className="space-y-3">
                  <div className="flex justify-between border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">{t("stats.value_mean")}</span>
                    <span className="font-heading font-black text-xl text-[#FDE047] tabular-nums">{imp.value_mean}</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">{t("stats.distance_center")}</span>
                    <span className="font-heading font-black text-xl tabular-nums">{imp.distance_pct}%</span>
                  </div>
                  <div className="flex justify-between border-b border-zinc-800 pb-2">
                    <span className="text-sm text-zinc-400">{t("stats.dispersion")}</span>
                    <span className="font-heading font-black text-xl tabular-nums">{imp.dispersion}%</span>
                  </div>
                  <div className="pt-1">
                    {hasTendency ? (
                      <div className="text-cyan-400 font-bold uppercase tracking-wide text-sm">
                        {t("stats.tendency_at")} {imp.clock_hour}{t("stats.oclock")}
                      </div>
                    ) : (
                      <div className="text-zinc-500 text-sm">{t("stats.no_tendency")}</div>
                    )}
                    <div className="text-xs text-zinc-600 mt-1">{imp.count} {t("stats.impacts_count")}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {stats.curve && stats.curve.length > 0 && (
            <div className="glass p-4 md:p-6">
              <h3 className="font-heading font-black text-lg uppercase tracking-tight mb-4">{t("stats.curve_title")}</h3>
              <ScoreCurve curve={stats.curve} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function History() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [viewerItem, setViewerItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState("history");
  const [sortBy, setSortBy] = useState("date_desc");
  const [period, setPeriod] = useState("1m");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (tab !== "stats") return;
    let active = true;
    setStatsLoading(true);
    api.get(`/stats?period=${period}`)
      .then(({ data }) => { if (active) setStats(data); })
      .catch(() => {})
      .finally(() => { if (active) setStatsLoading(false); });
    return () => { active = false; };
  }, [tab, period]);

  const opponentName = (it) => {
    if (it.type !== "match" || !Array.isArray(it.players)) return "";
    const opp = it.players.find((p) => p.user_id !== it.me_id);
    return opp ? opp.nickname : "";
  };

  // Moyenne par flèche : comparable entre 1 série (315) et 2 séries (545)
  const avgArrow = (it) => {
    const raw = it.scores;
    if (!Array.isArray(raw) || !raw.length) return null;
    const series = Array.isArray(raw[0]) && Array.isArray(raw[0][0]) ? raw : [raw];
    let sum = 0, n = 0;
    for (const serie of series)
      for (const end of serie || [])
        for (const v of end || [])
          if (v !== null && v !== undefined) { sum += v; n++; }
    return n ? sum / n : null;
  };

  const sortedItems = React.useMemo(() => {
    const arr = [...items];
    const ts = (x) => { const d = new Date(x.created_at).getTime(); return isNaN(d) ? 0 : d; };
    // les items sans flèches (matchs) partent en fin de tri par score
    const av = (x) => { const a = avgArrow(x); return a === null ? -1 : a; };
    if (sortBy === "date_asc") arr.sort((a, b) => ts(a) - ts(b));
    else if (sortBy === "score_desc") arr.sort((a, b) => av(b) - av(a) || ts(b) - ts(a));
    else if (sortBy === "score_asc") arr.sort((a, b) => av(a) - av(b) || ts(b) - ts(a));
    else arr.sort((a, b) => ts(b) - ts(a));
    return arr;
  }, [items, sortBy]);

  const load = async () => {
    try {
      const { data } = await api.get("/history");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/history/${toDelete.type}/${toDelete.id}`);
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
      toast.success(t("history.deleted"));
    } catch {
      toast.error(t("history.delete_error"));
    } finally {
      setDeleting(false);
      setToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grain text-white">
      {viewerItem && <HistoryTargetViewer item={viewerItem} onClose={() => setViewerItem(null)} />}
      <header className="sticky top-0 z-20 glass-header">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <button data-testid="history-back" onClick={() => navigate("/dashboard")} className="p-2 hover:bg-zinc-900 rounded-md">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <span className="font-heading font-black text-xl uppercase tracking-tight">{t("history.title")}</span>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-12 space-y-6">
        <div>
          <div className="text-cyan-400 text-xs uppercase tracking-[0.3em] mb-2">{t("history.label")}</div>
          <h1 className="font-heading font-black text-5xl uppercase tracking-tight">{t("history.heading")}</h1>
        </div>

        <div className="flex gap-2 border-b border-zinc-800">
          {[["history", t("stats.tab_history")], ["stats", t("stats.tab_stats")]].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-wide border-b-2 -mb-px transition-colors ${tab === k ? "border-cyan-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              {lbl}
            </button>
          ))}
        </div>

        {tab === "stats" && (
          <StatsPanel stats={stats} loading={statsLoading} period={period} onPeriod={setPeriod} />
        )}

        {tab === "history" && loading && <div className="text-zinc-500 uppercase tracking-widest">{t("common.loading")}</div>}

        {tab === "history" && !loading && items.length === 0 && (
          <div className="glass p-10 text-center">
            <Clock className="h-8 w-8 text-zinc-600 mx-auto mb-3" />
            <div className="text-zinc-400">{t("history.empty")}</div>
          </div>
        )}

        {tab === "history" && !loading && items.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mr-1">{t("stats.sort")}</span>
            {[
              ["date_desc", t("stats.sort_date_desc")],
              ["date_asc", t("stats.sort_date_asc")],
              ["score_desc", t("stats.sort_score_desc")],
              ["score_asc", t("stats.sort_score_asc")],
            ].map(([k, lbl]) => (
              <button key={k} onClick={() => setSortBy(k)}
                className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${sortBy === k ? "bg-cyan-400 text-black" : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"}`}>
                {lbl}
              </button>
            ))}
          </div>
        )}

        {tab === "history" && (
        <div className="space-y-2">
          {sortedItems.map((it) => {
            const Icon = ICONS[it.format] || TargetIcon;
            return (
              <div
                key={it.id}
                data-testid={`history-item-${it.id}`}
                className="group flex items-center gap-3 bg-zinc-900/30 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-md transition-all"
              >
                <button
                  onClick={() => navigate(it.type === "match" ? `/match/${it.id}` : `/solo/${it.id}`)}
                  className="flex-1 flex items-center gap-4 text-left px-4 py-4"
                >
                  <div className="h-10 w-10 rounded-md bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      {it.type === "match" ? (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-heading font-black uppercase tracking-tight text-lg">{t("history.match_word")}</span>
                          {opponentName(it) && (
                            <span className="font-heading font-black uppercase tracking-tight text-lg text-zinc-400 truncate">· {opponentName(it)}</span>
                          )}
                          {it.status === "finished" && it.winner_id && (
                            it.winner_id === it.me_id
                              ? <Crown className="h-5 w-5 text-[#FDE047] shrink-0" />
                              : <XCircle className="h-5 w-5 text-red-500 shrink-0" />
                          )}
                        </div>
                      ) : (
                        <div className="font-heading font-black uppercase tracking-tight text-lg truncate">
                          {it.session_name || t("dashboard.tile_solo_title")}
                        </div>
                      )}
                      <div className={`text-xs uppercase tracking-[0.2em] px-2 py-0.5 rounded ${it.status === "finished" ? "bg-cyan-400/10 text-cyan-400" : "bg-zinc-800 text-zinc-400"}`}>
                        {it.status}
                      </div>
                      {it.score != null && it.score > 0 && (
                        <div className="font-heading font-black text-cyan-400 text-lg tabular-nums">{it.score} pts</div>
                      )}
                    </div>
                    <div className="text-sm text-zinc-500 mt-1">
                      {fmtDate(it.created_at)}
                      {it.type === "match" && it.players && <> · {it.players.length} {t("history.players_suffix")}</>}
                      {it.code && <> · <span className="font-mono tracking-widest">{it.code}</span></>}
                    </div>
                  </div>
                  <div className="text-zinc-600 text-xs uppercase tracking-[0.2em] hidden sm:block">{t("history.open")}</div>
                </button>
                {it.type === "solo" && it.coords && it.coords.length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setViewerItem(it); }}
                    title={t("misc.view_impacts")}
                    className="p-2 rounded-md text-zinc-500 hover:text-cyan-400 hover:bg-cyan-400/10 transition-colors"
                  >
                    <Crosshair className="h-4 w-4" />
                  </button>
                )}
                <button
                  data-testid={`history-delete-${it.id}`}
                  onClick={(e) => { e.stopPropagation(); setToDelete(it); }}
                  title={t("history.delete")}
                  className="mr-3 p-2 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
        )}
      </main>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-dialog" className="bg-zinc-950 border border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-black text-2xl uppercase tracking-tight">{t("history.delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">{t("history.delete_confirm_desc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel" className="bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800">
              {t("history.delete_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              disabled={deleting}
              onClick={confirmDelete}
              className="bg-red-500 text-[#fff] hover:bg-red-600"
            >
              {deleting ? "..." : t("history.delete_ok")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}